import { DisplayButton } from '@xo-union/tk-ui-links';
import { Caption } from '@xo-union/tk-ui-typography';
import RightRail, { Header } from '../../../libs/gs-component-right-rail';
import React, { RefObject, useMemo, Dispatch, SetStateAction } from 'react';
import { Link } from 'react-router-dom';
import { PagesFragment, Wws_Page, usePagesQuery } from '../../../generated/graphql';
import { getTargetOrigin } from '../../../helpers/getTargetOrigin';
import { reloadIframe } from '../../../helpers/iframeHelpers';
import { WEDDING_PAGE_TYPE } from '../../../helpers/pages';
import { useRsvpIsVisible } from '../../../helpers/useRsvpIsVisible';
import { useWorkflowUnstartedState } from '../../RsvpPageCard/utils';
import { previewRoute } from '../../../helpers/routes';
import { PaperCallout } from '../../CalloutCard/PaperCallout';
import styles from './previewRightRail.scss';
import Icon from '@xo-union/tk-component-icons';
import AddPageButton from '../../ManagePagesRail/AddPageButton';
import { compose } from '@xo-union/react-css-modules';
import StatusText from '../../Card/subcomponents/StatusText';
import cx from 'classnames';
import { useWebsiteLayoutContext } from '../../../context/WebsiteLayoutContext';
import DressCodePage from './DressCodePage';

const HiddenBadge = () => (
  <StatusText className={styles.hiddenBadge} data-testid="hidden-badge">
    <Icon name="eye_closed" size="sm" />
    <span className={styles.hiddenBadge__text}>{'Hidden'}</span>
  </StatusText>
);

interface SubNavButtonProps {
  title: string;
  description?: string;
  onClick: () => void;
}

const SubNavButton: React.FC<SubNavButtonProps> = ({ title, description, onClick }) => (
  <DisplayButton
    color="primary"
    className={styles.previewRightRail__subNavButton}
    classes={compose({ 'tappable-area': styles.previewRightRail__subNavButton__tappableArea })}
    onClick={onClick}
    data-testid="change-theme-cta"
  >
    <div className={styles.previewRightRail__subNavButton__content}>
      <div className={styles.previewRightRail__subNavButton__content__row}>
        <span className={styles.previewRightRail__subNavButton__title}>{title}</span>
      </div>
      {description && (
        <div className={styles.previewRightRail__subNavButton__content__row}>
          <span className={styles.previewRightRail__subNavButton__description}>{description}</span>
        </div>
      )}
    </div>
    <Icon name="caret_right" size="md" />
  </DisplayButton>
);

type PageLinkProps = {
  onClick: () => void;
  title: string;
  routeName: string;
  isHidden: boolean;
};

const PageLink: React.FC<PageLinkProps> = ({ onClick, title, routeName, isHidden }) => {
  const pageLinkClassNames = cx(styles.previewRightRail__subNavButton, {
    [styles.previewRightRail__subNavButton__hidden]: isHidden,
  });

  return (
    <Link
      className={pageLinkClassNames}
      to={{ pathname: previewRoute, search: `?location=${routeName}` }}
      onClick={onClick}
    >
      <div className={styles.previewRightRail__pageLinkContainer}>
        <span className={styles.previewRightRail__subNavButton__content}>
          <div className={styles.previewRightRail__subNavButton__content__row}>
            <span className={styles.previewRightRail__subNavButton__title}>{title}</span>
            {isHidden && <HiddenBadge />}
          </div>
        </span>
        <Icon name="caret_right" size="md" />
      </div>
    </Link>
  );
};

type Props = {
  pageId?: number | string;
  editSiteOpen: boolean;
  toggleEditWebsiteHandler: () => void;
  toggleEditPageHandler: () => void;
  toggleReorderPagesHandler: () => void;
  toggleSettingsRailHandler: () => void;
  toggleChangeThemeRailHandler: () => void;
  setCurrentPage: Dispatch<SetStateAction<PagesFragment | Wws_Page | string | undefined>>;
  showRsvpEmptyState: boolean;
  setShowRsvpEmptyState: (state: boolean) => void;
  addPageToggle: (page?: PagesFragment) => void;
  iframeRef: RefObject<HTMLIFrameElement>;
  togglePageOpen?: (pageId: number) => void;
};

const useNavLinks = (): PagesFragment[] => {
  const { data } = usePagesQuery();

  let pages = (data?.pages || []).filter(p => {
    if (!p.title) {
      return false;
    }

    return true;
  });

  // Rename Our Wedding to Home for legacy reasons
  pages = useMemo(
    () =>
      pages?.map(p => {
        if (p.type === WEDDING_PAGE_TYPE) {
          return {
            ...p,
            title: 'Home',
          };
        }

        return p;
      }),
    [pages]
  );

  return pages;
};

const PreviewRightRail: React.FC<Props> = ({
  editSiteOpen,
  toggleEditWebsiteHandler,
  toggleEditPageHandler,
  toggleReorderPagesHandler,
  toggleSettingsRailHandler,
  toggleChangeThemeRailHandler,
  pageId,
  setCurrentPage,
  iframeRef,
  showRsvpEmptyState,
  addPageToggle,
  setShowRsvpEmptyState,
  togglePageOpen,
  children,
}) => {
  const navPages = useNavLinks();
  const { currentLayout } = useWebsiteLayoutContext();

  const { loading: rsvpLoading, isUnstartedState } = useWorkflowUnstartedState();
  const isRsvpVisible = useRsvpIsVisible();

  const subNavClick = (page: PagesFragment | Wws_Page | string) => {
    setCurrentPage(page);
    toggleEditPageHandler();

    if (iframeRef?.current?.contentWindow) {
      let currentPageMessageData: PagesFragment | Wws_Page | { routeName: string } | null = null;

      if (typeof page === 'string') {
        currentPageMessageData = {
          routeName: page,
        };
      } else {
        currentPageMessageData = page;
      }

      if (currentPageMessageData) {
        iframeRef.current.contentWindow.postMessage(
          JSON.stringify({
            type: 'currentPage',
            data: currentPageMessageData,
          }),
          getTargetOrigin()
        );
      }
    }
  };

  const onPageLinkClick = (page: PagesFragment) => {
    if (showRsvpEmptyState) {
      setShowRsvpEmptyState(false);
    }
    subNavClick(page);
  };

  const onRsvpLinkClick = () => {
    if (isUnstartedState) {
      setShowRsvpEmptyState(true);
      toggleEditPageHandler();
    } else {
      subNavClick('rsvp');
    }
  };
  const isSinglePageSite = currentLayout === 'single-page';
  const pagesOrSections = isSinglePageSite ? 'Sections' : 'Pages';
  const lowerCasePagesOrSections = pagesOrSections.toLowerCase();
  const pageOrSection = pagesOrSections.slice(0, -1);
  // Constants and utility for Dress Code page detection
  const DRESS_CODE_PAGE_TYPE = 'DressCodePage';
  const DRESS_CODE_TITLE = 'dress code';

  function isDressCodePage(page: PagesFragment): boolean {
    const titleNorm = (page.title || '')
      .toLowerCase()
      .replace(/\s+/g, ' ')
      .trim();
    return page.type === DRESS_CODE_PAGE_TYPE || titleNorm === DRESS_CODE_TITLE || titleNorm.includes(DRESS_CODE_TITLE);
  }

  const showAddDressCodePage = useMemo(() => !navPages.some(isDressCodePage), [navPages]);

  const handleCreatedPage = (page: PagesFragment | Wws_Page) => {
    // Reload iframe to show the new page in navigation (same as AddPage onSaveSuccess)
    reloadIframe(iframeRef);
    if (page.id && togglePageOpen) {
      togglePageOpen(page.id);
    }
  };

  return (
    <RightRail open={editSiteOpen} overlay={false}>
      <Header onClose={toggleEditWebsiteHandler}>Edit website</Header>
      <div className={`${styles.editRailContainer}`}>
        <Caption size="lg" className={styles.previewRightRail__sectionTitle}>
          Website settings
        </Caption>
        <div>
          <SubNavButton
            title="Design"
            description="Update your design, color and layout"
            onClick={toggleChangeThemeRailHandler}
          />
          <SubNavButton
            title="Privacy & URL"
            description="Update search and password preferences"
            onClick={toggleSettingsRailHandler}
          />
          {showAddDressCodePage && <DressCodePage onCreatedPage={handleCreatedPage} />}
        </div>
        <Caption size="lg" className={styles.previewRightRail__sectionTitle}>
          {pagesOrSections}
          <DisplayButton
            color="primary"
            className={styles.previewRightRail__reorderButton}
            onClick={toggleReorderPagesHandler}
            data-testid="reorder-pages-cta"
          >
            {`Reorder ${lowerCasePagesOrSections}`}
          </DisplayButton>
        </Caption>
        <div>
          {navPages?.map(page => (
            <PageLink
              key={page.id.toString()}
              onClick={() => onPageLinkClick(page)}
              title={page.title || ''}
              routeName={page.routeName || ''}
              isHidden={!page.show}
            />
          ))}
          {!rsvpLoading && (
            <PageLink
              key="rsvp"
              onClick={onRsvpLinkClick}
              title="RSVP"
              routeName={isUnstartedState ? '' : 'rsvp'}
              isHidden={!isRsvpVisible}
            />
          )}
          <div className={styles.previewRightRail__addPageButtonContainer}>
            <AddPageButton children={`Add a Custom ${pageOrSection}`} block noMargin onClick={() => addPageToggle()} />
          </div>
          <PaperCallout />
        </div>
      </div>
      {children}
    </RightRail>
  );
};

export default PreviewRightRail;
